import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { reactionsApi, type ReactionSummary } from '@/api/reactions';
import { useAuthStore } from '@/store/authStore';
import { apiClient } from '@/api/client';
import type { ReactionRow } from '@/types';

const FALLBACK_QUICK = ['👍', '❤️', '😂', '🎉', '😮', '😢', '🔥', '👏'];

// Shared cache — refreshed on login and when admin updates
let cachedEmojis: string[] | null = null;
let pendingFetch: Promise<string[]> | null = null;

async function loadAvailableEmojis(): Promise<string[]> {
  if (cachedEmojis) return cachedEmojis;
  if (pendingFetch) return pendingFetch;
  pendingFetch = apiClient
    .get<Array<{ emoji: string; name: string }>>('/api/reactions/available')
    .then((res) => {
      cachedEmojis = res.data.map((x) => x.emoji);
      return cachedEmojis;
    })
    .catch(() => FALLBACK_QUICK)
    .finally(() => { pendingFetch = null; });
  return pendingFetch;
}

/** Clear cache so next consumer refetches. Call after admin changes emojis. */
export function refreshReactionEmojis() {
  cachedEmojis = null;
}

interface Props {
  /** Either messageId (channel) or dmId (DM). */
  messageId?: string;
  dmId?: string;
  initial?: ReactionRow[];
  /** Compact rows or full picker */
  align?: 'left' | 'right';
  /** If false, hide the "+" button (reactions disabled in this channel) */
  enabled?: boolean;
}

function rowsToSummary(rows: ReactionRow[] | undefined): ReactionSummary[] {
  if (!rows || rows.length === 0) return [];
  const m = new Map<string, string[]>();
  for (const r of rows) {
    if (!m.has(r.emoji)) m.set(r.emoji, []);
    m.get(r.emoji)!.push(r.userId);
  }
  return Array.from(m.entries()).map(([emoji, users]) => ({ emoji, count: users.length, users }));
}

export default function MessageReactions({ messageId, dmId, initial, align = 'left', enabled = true }: Props) {
  const { user } = useAuthStore();
  const [summary, setSummary] = useState<ReactionSummary[]>(() => rowsToSummary(initial));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [emojis, setEmojis] = useState<string[]>(cachedEmojis ?? FALLBACK_QUICK);

  useEffect(() => {
    let cancelled = false;
    loadAvailableEmojis().then((list) => { if (!cancelled) setEmojis(list); });
    return () => { cancelled = true; };
  }, []);

  // Sync from prop changes (after socket-driven reload)
  useEffect(() => {
    setSummary(rowsToSummary(initial));
  }, [initial]);

  // Listen for live updates from socket bridge
  useEffect(() => {
    const onMsg = (e: Event) => {
      const detail = (e as CustomEvent).detail as { messageId?: string; dmId?: string; summary: ReactionSummary[] };
      if (messageId && detail.messageId === messageId) setSummary(detail.summary);
      if (dmId && detail.dmId === dmId) setSummary(detail.summary);
    };
    if (messageId) window.addEventListener('reaction:update', onMsg);
    if (dmId) window.addEventListener('dm:reaction:update', onMsg);
    return () => {
      if (messageId) window.removeEventListener('reaction:update', onMsg);
      if (dmId) window.removeEventListener('dm:reaction:update', onMsg);
    };
  }, [messageId, dmId]);

  const toggle = async (emoji: string) => {
    // Optimistic update
    const me = user?.id;
    if (!me) return;
    setSummary((prev) => {
      const found = prev.find((r) => r.emoji === emoji);
      if (found) {
        if (found.users.includes(me)) {
          const users = found.users.filter((u) => u !== me);
          if (users.length === 0) return prev.filter((r) => r.emoji !== emoji);
          return prev.map((r) => (r.emoji === emoji ? { ...r, users, count: users.length } : r));
        }
        return prev.map((r) =>
          r.emoji === emoji ? { ...r, users: [...r.users, me], count: r.count + 1 } : r,
        );
      }
      return [...prev, { emoji, users: [me], count: 1 }];
    });
    try {
      const result = messageId
        ? await reactionsApi.toggleChannelReaction(messageId, emoji)
        : dmId
        ? await reactionsApi.toggleDmReaction(dmId, emoji)
        : null;
      if (result) setSummary(result.summary);
    } catch {
      // Re-fetch authoritative state on failure
      try {
        const fresh = messageId
          ? await reactionsApi.getMessageReactions(messageId)
          : dmId
          ? await reactionsApi.getDmReactions(dmId)
          : [];
        setSummary(fresh);
      } catch { /* ignore */ }
    }
    setPickerOpen(false);
  };

  // Без реакций — ничего не рендерим (кнопка "+" убрана в Phase 26.2;
  // реакции ставятся через контекстное меню на сообщении).
  if (summary.length === 0) return null;

  return (
    <div className={clsx('relative inline-flex flex-wrap items-center gap-0.5', align === 'right' ? 'justify-end' : 'justify-start')}>
      {summary.map((r) => {
        const mine = user && r.users.includes(user.id);
        return (
          <button
            key={r.emoji}
            type="button"
            onClick={() => toggle(r.emoji)}
            className={clsx(
              'text-[11px] px-1 py-0 rounded-full border leading-4 flex items-center gap-0.5 transition-colors',
              mine
                ? 'bg-brand-600/30 border-brand-500 text-brand-200'
                : 'bg-surface-800/60 border-surface-700 text-surface-300 hover:border-surface-600',
            )}
          >
            <span className="text-[11px]">{r.emoji}</span>
            <span className="text-[9px] font-mono opacity-70">{r.count}</span>
          </button>
        );
      })}
      {enabled && pickerOpen && (
        <div className="absolute -top-9 z-20 flex gap-0.5 bg-surface-800 border border-surface-700 rounded-xl px-1.5 py-1 shadow-xl">
          {emojis.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => toggle(e)}
              className="hover:bg-surface-700 rounded-lg w-7 h-7 flex items-center justify-center text-base"
            >
              {e}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
